import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { API, AttachmentDTO, AttachmentType, DisplayType } from '../../../../../services/api/api';
import { ToasterService, ToastType } from '../../../../../services/toaster/toaster.service';
import { UtilService } from '../../../../../services/util.service';
import * as _ from 'lodash';
import { ErrorService } from '../../../../../services/error.service';
import { Mutable } from '../../../../../services/global/global-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentService } from '../../../../../services/attachment.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { enumKeys } from '../../../../../services/global/global-methods';

@Component({
  selector: 'app-attachment-url',
  templateUrl: './attachment-url.component.html',
  styleUrls: ['./attachment-url.component.scss'],
  standalone: false,
})
export class AttachmentUrlComponent implements OnInit {
  displaySelection: DisplayType[];
  urlReg = '(http|https):\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,4}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)';
  form = new UntypedFormGroup({
    Title: new UntypedFormControl('', [Validators.required]),
    Description: new UntypedFormControl('', []),
    Link: new UntypedFormControl('', [Validators.required, Validators.pattern(this.urlReg)]),
    DisplayTypeForm: new UntypedFormControl(),
  });

  constructor(
    private api: API,
    private toaster: ToasterService,
    private util: UtilService,
    private errorHandler: ErrorService,
    private translate: TranslateService,
    private attachmentService: AttachmentService,
    private destroyRef: DestroyRef
  ) {}

  _attachment: AttachmentDTO;

  get attachment(): AttachmentDTO {
    return this._attachment;
  }

  @Input() set attachment(val: AttachmentDTO) {
    this._attachment = val;
    if (this._attachment) {
      this.updateForm();
    }
  }

  get Title() {
    return this.form.get('Title');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Link() {
    return this.form.get('Link');
  }

  get DisplayTypeForm() {
    return this.form.get('DisplayTypeForm');
  }

  ngOnInit(): void {
    if (this.attachment?.id) {
      this.initEnum();
      this.Link.disable();
    }
    if (!this.attachment) {
      this.attachment = new AttachmentDTO();
    }
    this.form = _.cloneDeep(this.form); // Prevent data binding
  }

  updateForm() {
    if (this.attachment?.id && this.attachment?.attachmentType === AttachmentType.LINK) {
      this.Title.setValue(this.attachment.name);
      this.Link.setValue(this.attachment.link);
      this.Description.setValue(this.attachment.description);
      this.DisplayTypeForm.setValue(this.attachment.displayType);
    }
  }

  initEnum() {
    const displayKeys = enumKeys(DisplayType);
    this.displaySelection = [];
    for (const val of displayKeys) {
      if (val === DisplayType.EMBED) {
        if (this.attachment.embeddable) {
          this.displaySelection.push(DisplayType[val]);
        }
      } else {
        this.displaySelection.push(DisplayType[val]);
      }
    }
    if (this.attachment?.displayType) {
      this.DisplayTypeForm.setValue(this.attachment.displayType);
    } else {
      this.DisplayTypeForm.setValue(this.displaySelection.find(x => x === DisplayType.LINK));
    }
  }

  saveUrlAttachment() {
    if (this.form.valid) {
      this.getChanges();
      if (this.attachment?.id) {
        this.api
          .shared_Attachment_editAttachment(this.attachment)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              this.attachment = data;
              this.attachmentService.attachmentChange.next({
                type: 'edit',
                attachment: this.attachment,
              });
              this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.successful') + '!');
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        const timer = setTimeout(() => {
          const loadingAttachment: Mutable<AttachmentDTO> = new AttachmentDTO();
          loadingAttachment.attachmentType = AttachmentType.LINK;
          this.attachmentService.attachmentChange.next({
            type: 'create',
            attachment: loadingAttachment,
          });
        }, 1000);
        this.api
          .shared_Attachment_createLinkAttachment(this.attachment)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: data => {
              this.attachment = data;
              clearTimeout(timer);
              this.attachmentService.attachmentChange.next({
                type: 'create',
                attachment: this.attachment,
              });
              this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.successful') + '!');
            },
            error: error => {
              clearTimeout(timer);
              const loadingAttachment: Mutable<AttachmentDTO> = new AttachmentDTO();
              loadingAttachment.attachmentType = AttachmentType.UPLOAD;
              loadingAttachment.id = -1;
              this.attachmentService.attachmentChange.next({
                type: 'create',
                attachment: loadingAttachment,
              });
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  pasteDataFromClipboard() {
    if (this.attachment?.id) {
      navigator.clipboard
        .readText()
        .then((input: string) => {
          this.Link.setValue(input);
        })
        .catch(() => {});
    }
  }

  private getChanges() {
    this.attachment.name = this.Title.value;
    this.attachment.description = this.Description.value;
    this.attachment.link = this.Link.value;
    this.attachment.displayType = this.DisplayTypeForm.value;
  }
}
